import { useState, useCallback, useRef, useEffect } from 'react';
import { mapValues, orderBy } from 'lodash';
import { QUERY_NAMES } from 'react/services/AudienceServiceHelper';
import { getSegmentInsightsData } from 'react/services/AudienceInsightsService';
import { ALL_INTERESTS, VIEW_TYPES_NAMES } from '../constants';
import {
  throwForInvalidDataResponse,
  transformInterestsData,
  sortAndNormalizeInterests,
  getNoDataMsg,
} from '../utils/audienceInsightsUtils';

/**
 * Hook for managing interests data fetching and transformations
 */
const useInterestsData = ({
  segment,
  audienceChannel,
  targetRatioObj,
  sortType,
  infraUtil,
  interestTypesMap,
  expelUserIfMissingData,
  categoryAffinityType = 'audience',
}) => {
  const [interests, setInterests] = useState(null);
  const [fullInterests, setFullInterests] = useState(null);
  const [noDataMsg, setNoDataMsg] = useState('');

  // Cache for top interests promises by type
  const topInterestsPromisesRef = useRef({});

  // Token to track the latest request and avoid race conditions
  const latestRequestTokenRef = useRef(0);

  // Clear cache when segment, channel, ratio, or category affinity (categories vs purchase) change
  useEffect(() => {
    topInterestsPromisesRef.current = {};
  }, [segment, audienceChannel, targetRatioObj, categoryAffinityType]);

  /**
   * Get top interests data by type (with caching).
   * For 'categories', an optional `overrideAffinity` ('audience' | 'purchase')
   * lets callers fetch a specific affinity type independently of the active tab,
   * with a per-affinity cache slot so both can coexist.
   */
  const getTopInterestsDataByType = useCallback(
    (type, overrideAffinity) => {
      const effectiveAffinity = type === 'categories' ? overrideAffinity || categoryAffinityType : null;
      const cacheKey = effectiveAffinity ? `${type}:${effectiveAffinity}` : type;

      if (topInterestsPromisesRef.current[cacheKey]) {
        return topInterestsPromisesRef.current[cacheKey];
      }

      const insightOpts = effectiveAffinity ? { categoryAffinityType: effectiveAffinity } : {};
      const promise = getSegmentInsightsData(segment, QUERY_NAMES[type], audienceChannel, targetRatioObj, insightOpts)
        .then(throwForInvalidDataResponse)
        .catch(expelUserIfMissingData)
        .then((data) => transformInterestsData(data.words || data.insights || [], type, type === 'websites'));

      topInterestsPromisesRef.current[cacheKey] = promise;
      return promise;
    },
    [segment, audienceChannel, targetRatioObj, expelUserIfMissingData, categoryAffinityType]
  );

  /**
   * Get promise of interests by type and topic.
   * `overrideAffinity` is forwarded to `getTopInterestsDataByType` (categories only).
   */
  const getPromiseOfInterestsByTypeAndTopic = useCallback(
    (type, topicId, overrideAffinity) =>
      getTopInterestsDataByType(type, overrideAffinity).then((dataByView) =>
        mapValues(dataByView, (data) => data[topicId] || [])
      ),
    [getTopInterestsDataByType]
  );

  /**
   * Get all interests by type and topic (combines top and tested interests)
   */
  const getAllInterestsByTypeAndTopic = useCallback(
    (type, topicId, getPromiseOfTestedInterestsByType) => {
      if (topicId === ALL_INTERESTS || topicId === 'All Categories') {
        return getPromiseOfInterestsByTypeAndTopic(type, topicId).then((topInterestsByView) =>
          getPromiseOfTestedInterestsByType(type).then((testedInterestsByView) =>
            // Merge tested interests with top interests, removing duplicates
            // Always iterate over expected view keys to handle empty testedInterestsByView
            mapValues({ forChart: [], forTable: [] }, (_, view) => {
              const testedArr = testedInterestsByView[view] || [];
              const topArr = topInterestsByView[view] || [];
              const combined = [...testedArr, ...topArr];
              // Remove duplicates by id
              const uniqueById = {};
              combined.forEach((item) => {
                if (!uniqueById[item.id]) {
                  uniqueById[item.id] = item;
                }
              });
              return Object.values(uniqueById);
            })
          )
        );
      }
      return getPromiseOfInterestsByTypeAndTopic(type, topicId);
    },
    [getPromiseOfInterestsByTypeAndTopic]
  );

  /**
   * Publish all interests to view state
   */
  const publishAllInterestsToView = useCallback(
    ({
      updateQueryObj = false,
      type,
      topicId = ALL_INTERESTS,
      lastViewType,
      setViewType,
      setQueryData,
      resetSelection,
      getPromiseOfTestedInterestsByType,
    }) => {
      if (!topicId || !type) return Promise.resolve();

      const token = ++latestRequestTokenRef.current;

      return getAllInterestsByTypeAndTopic(type, topicId, getPromiseOfTestedInterestsByType)
        .then((data) => {
          if (token !== latestRequestTokenRef.current) return null;
          return sortAndNormalizeInterests(data, sortType, infraUtil);
        })
        .then((normalizedData) => {
          if (token !== latestRequestTokenRef.current || !normalizedData) return;

          const { forChart, forTable } = normalizedData;
          const isCategories = type === 'categories';

          // For categories, store full list and limit display to top 200 by highest audience skew
          if (isCategories && forChart?.length > 0) {
            // Sort by highlighted first (desc), then by uniqueness-index (audience skew) descending
            const sortedBySkew = orderBy(forChart, ['highlighted', 'uniqueness-index'], ['desc', 'desc']);
            setFullInterests(sortedBySkew);
            setInterests(sortedBySkew.length > 200 ? sortedBySkew.slice(0, 200) : sortedBySkew);
          } else {
            setFullInterests(forChart || []);
            setInterests(forChart || []);
          }

          if (!forChart || forChart.length === 0) {
            setNoDataMsg(getNoDataMsg(type, interestTypesMap));
          } else {
            setNoDataMsg('');
          }

          // Set view type based on topic
          const newViewType = topicId === ALL_INTERESTS ? lastViewType : VIEW_TYPES_NAMES.chart;
          setViewType(newViewType);

          if (updateQueryObj && forTable) {
            setQueryData((prev) => ({
              ...prev,
              dataArray: forTable,
            }));
          }

          resetSelection();
        });
    },
    [sortType, infraUtil, interestTypesMap, getAllInterestsByTypeAndTopic]
  );

  /**
   * Drop the currently published interests so consumers fall back to their loading
   * state. `interests` and `fullInterests` are published as a pair, so they must be
   * cleared as a pair — otherwise a table fed by `fullInterests` keeps painting the
   * previous tab's rows behind its loader.
   */
  const resetInterests = useCallback(() => {
    setInterests(null);
    setFullInterests(null);
    setNoDataMsg('');
  }, []);

  /**
   * Clear the interests cache for a specific type or all types
   */
  const clearInterestsCache = useCallback((type = null) => {
    if (type) {
      delete topInterestsPromisesRef.current[type];
    } else {
      topInterestsPromisesRef.current = {};
    }
  }, []);

  return {
    interests,
    fullInterests,
    setInterests,
    resetInterests,
    noDataMsg,
    setNoDataMsg,
    getTopInterestsDataByType,
    getPromiseOfInterestsByTypeAndTopic,
    getAllInterestsByTypeAndTopic,
    publishAllInterestsToView,
    clearInterestsCache,
  };
};

export default useInterestsData;
